import { useContext, useMemo, useState } from 'react'
import { ModalContext } from '../ModalContext'

import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function ProgramLevelScoreSelectorModal() {
    const { modalData, HideModal } = useContext(ModalContext)

    const programLevels = useMemo(() => {
        return [...(modalData.programLevels ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
    }, [modalData.programLevels])

    const scoreMin = useMemo(() => {
        if (programLevels.length === 0) return 0
        return Math.min(...programLevels.map((level) => level.levelScoreLowerBound))
    }, [programLevels])

    const scoreMax = useMemo(() => {
        if (programLevels.length === 0) return 0
        return Math.max(...programLevels.map((level) => level.levelScoreUpperBound))
    }, [programLevels])

    const totalScoreSpan = scoreMax - scoreMin + 1

    const [selectedScore, setSelectedScore] = useState(() => {
        const initialScore = modalData.programLevelScore ?? scoreMin
        return Math.min(scoreMax, Math.max(scoreMin, initialScore))
    })

    const selectedProgramLevel = useMemo(() => {
        return programLevels.find((level) => (
            level.levelScoreLowerBound <= selectedScore
            && selectedScore <= level.levelScoreUpperBound
        )) ?? null
    }, [programLevels, selectedScore])

    const GetLevelWidthPercent = (programLevel) => {
        if (totalScoreSpan <= 0) return 0
        const levelSpan = programLevel.levelScoreUpperBound - programLevel.levelScoreLowerBound + 1
        return (levelSpan / totalScoreSpan) * 100
    }

    const ClampScore = (score) => Math.min(scoreMax, Math.max(scoreMin, score))

    const OnScoreInputChange = (e) => {
        const value = e.target.value

        if (value === '') {
            setSelectedScore(scoreMin)
            return
        }

        const parsed = parseInt(value, 10)
        if (Number.isNaN(parsed)) return

        setSelectedScore(ClampScore(parsed))
    }

    const OnSelect = () => {
        modalData.setProgramLevelScore(selectedScore)
        HideModal()
    }

    return (
        <>
            <div className="text-center text-lg font-semibold leading-6 text-gray-900">
                Program Level Score Selector
            </div>

            <div className="space-y-3">
                <div className="text-center space-y-1">
                    <input
                        type="number"
                        min={scoreMin}
                        max={scoreMax}
                        value={selectedScore}
                        onChange={OnScoreInputChange}
                        className="
                            w-28 mx-auto block text-center text-3xl font-semibold text-gray-900
                            border border-gray-300 rounded-lg py-1
                            focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400
                            [appearance:textfield]
                            [&::-webkit-outer-spin-button]:appearance-none
                            [&::-webkit-inner-spin-button]:appearance-none
                        "
                    />
                </div>

                <div className="flex overflow-hidden rounded-lg pb-2">
                    {programLevels.map((programLevel, index) => {
                        const isSelected = selectedProgramLevel?.programLevelId === programLevel.programLevelId
                        const isFirst = index === 0
                        const isLast = index === programLevels.length - 1

                        return (
                            <div
                                key={programLevel.programLevelId}
                                onClick={() => setSelectedScore(programLevel.levelScoreLowerBound)}
                                className={`
                                    flex items-center justify-center py-2 px-1 text-center transition-all cursor-pointer
                                    ${isFirst ? 'rounded-l-lg' : ''}
                                    ${isLast ? 'rounded-r-lg' : ''}
                                    ${isSelected ? 'ring-2 ring-inset ring-gray-900 z-10' : 'opacity-70 hover:opacity-90'}
                                `}
                                style={{
                                    backgroundColor: programLevel.colorHex,
                                    width: `${GetLevelWidthPercent(programLevel)}%`,
                                }}
                            >
                                <p className={`text-xs text-gray-800 ${isSelected ? 'font-semibold' : ''}`}>
                                    {programLevel.name}
                                </p>
                            </div>
                        )
                    })}
                </div>

                <input
                    type="range"
                    min={scoreMin}
                    max={scoreMax}
                    value={selectedScore}
                    onChange={(e) => setSelectedScore(parseInt(e.target.value, 10))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-400"
                />
            </div>

            <div className='pt-2 w-1/2 mx-auto'>
                <BrandColorButton
                    text="Save"
                    action={OnSelect}
                />
            </div>
        </>
    )
}
